import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { listsKeys, restaurantKeys } from '@/api/keys';
import {
  CreateListRequest,
  CreateListResponse,
  CreateAndAddListRequest,
  CreateAndAddListResponse,
  RenameListResponse,
  ShareListRequest,
  ShareListResponse,
  CopyListResponse,
  ReorderLocationsRequest,
  ReorderRestaurantsRequest,
  AddTagResponse,
  RemoveTagResponse,
  RestaurantListDetails,
} from '@/types/lists/restaurantList';
import { SearchResponse } from '@/types/server/restaurantApiResponses';
import { Restaurant } from '@/types/restaurant';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { appsFlyerService } from '@/lib/appsflyer';

/**
 * The restaurant detail page (`useGetRestaurantDetails`) reads `isBookmarked`
 * from its own cache entry, which the search-cache patches below never touch —
 * without this the profile's save button stayed unfilled until a hard refetch.
 *
 * Cancels any in-flight detail fetch first. A seeded profile is interactive
 * while its `/summary` request is still running; that response was computed
 * before this save, so letting it land would overwrite the patched
 * `isBookmarked` with the stale value. Callers also invalidate the detail key
 * in `onSettled` so the profile reconciles with the server afterward.
 */
async function setDetailBookmarkStatus(
  queryClient: ReturnType<typeof useQueryClient>,
  restaurantId: number,
  isBookmarked: boolean
) {
  const key = restaurantKeys.detail(restaurantId);
  // `exact: true` — the detail key is a prefix of the ratings/menu-items keys,
  // and a save must not cancel those fetches.
  await queryClient.cancelQueries({ queryKey: key, exact: true });
  // Preserve the entry's `dataUpdatedAt`. `setQueryData` otherwise stamps
  // `Date.now()`, which would erase the seeded-entry marker
  // (`SEEDED_DETAIL_UPDATED_AT`) while the summary refetch is still in flight
  // and make the profile's Learn More chips think real data had landed.
  queryClient.setQueryData<Restaurant>(
    key,
    (oldData) => (oldData ? { ...oldData, isBookmarked } : oldData),
    { updatedAt: queryClient.getQueryState(key)?.dataUpdatedAt }
  );
}

/**
 * Helper to update restaurant bookmark status in search cache
 */
async function updateRestaurantBookmarkStatus(
  queryClient: ReturnType<typeof useQueryClient>,
  restaurantId: number
) {
  queryClient.setQueriesData<SearchResponse>(
    { queryKey: restaurantKeys.searches() },
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        restaurants: oldData.restaurants.map((restaurant) =>
          restaurant.id === restaurantId
            ? { ...restaurant, isBookmarked: true }
            : restaurant
        ),
      };
    }
  );
  await setDetailBookmarkStatus(queryClient, restaurantId, true);
}

/**
 * Helper to clear restaurant bookmark status in search cache.
 * Used when a restaurant is removed from a list — the icon should go dark
 * immediately. The server re-fetch (invalidateQueries in onSettled) will
 * correct this if the restaurant is still bookmarked in another list.
 */
async function clearRestaurantBookmarkStatus(
  queryClient: ReturnType<typeof useQueryClient>,
  restaurantId: number
) {
  queryClient.setQueriesData<SearchResponse>(
    { queryKey: restaurantKeys.searches() },
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        restaurants: oldData.restaurants.map((restaurant) =>
          restaurant.id === restaurantId
            ? { ...restaurant, isBookmarked: false }
            : restaurant
        ),
      };
    }
  );
  await setDetailBookmarkStatus(queryClient, restaurantId, false);
}

/**
 * Hook to create a new list
 */
export function useCreateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateListRequest) =>
      apiFetch<CreateListResponse>('/lists/', {
        method: 'POST',
        body: data,
      }),
    onSuccess: (data, variables) => {
      // Track list creation
      analytics.capture(AnalyticsEvents.LIST_CREATED, {
        list_id: data.urlKey,
        is_collaborative: false,
      });

      // AppsFlyer: Track list creation event
      appsFlyerService.logListCreated('personal', false);

      // Invalidate all list queries (lists, search, details, etc.)
      queryClient.invalidateQueries({ queryKey: listsKeys.all });
    },
  });
}

/**
 * Hook to create a new list and add a restaurant to it
 */
export function useCreateAndAddToList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAndAddListRequest) =>
      apiFetch<CreateAndAddListResponse>('/lists/add/', {
        method: 'POST',
        body: data,
      }),
    onSuccess: async (data, variables) => {
      // Track list creation and restaurant addition
      analytics.capture(AnalyticsEvents.LIST_CREATED, {
        list_id: data.urlKey,
        is_collaborative: false,
      });
      analytics.capture(AnalyticsEvents.RESTAURANT_ADDED_TO_LIST, {
        list_id: data.urlKey,
        restaurant_id: variables.restaurantId,
      });

      // AppsFlyer: Track list creation event
      appsFlyerService.logListCreated('personal', false);

      // Invalidate all list queries (lists, search, details, etc.)
      queryClient.invalidateQueries({ queryKey: listsKeys.all });
      // Update bookmark status in search results and the profile detail cache
      await updateRestaurantBookmarkStatus(queryClient, variables.restaurantId);
    },
    onSettled: (_data, _error, variables) => {
      // Reconcile the profile with the server (isBookmarked is authoritative there).
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurantId),
        exact: true,
      });
    },
  });
}

/**
 * Hook to delete a list
 */
export function useDeleteList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<void>(`/lists/${urlKey}/delete/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      // Invalidate search cache so bookmark icons reflect the correct state after
      // all restaurants in this list are removed. We don't know which restaurants
      // were in the list, so we can't update them selectively.
      queryClient.invalidateQueries({ queryKey: restaurantKeys.searches() });
    },
  });
}

/**
 * Hook to rename a list
 */
export function useRenameList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, listName }: { urlKey: string; listName: string }) =>
      apiFetch<RenameListResponse>(`/lists/${urlKey}/rename/`, {
        method: 'PATCH',
        body: { listName },
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to add a restaurant to a list by restaurant ID
 */
export function useAddRestaurantWithId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, restaurantId }: { urlKey: string; restaurantId: number }) =>
      apiFetch<void>(`/lists/${urlKey}/add/`, {
        method: 'PATCH',
        body: { restaurantId },
      }),
    onSuccess: async (_, variables) => {
      // Track restaurant added to list
      analytics.capture(AnalyticsEvents.RESTAURANT_ADDED_TO_LIST, {
        list_id: variables.urlKey,
        restaurant_id: variables.restaurantId,
      });

      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
      // Update bookmark status in search results and the profile detail cache
      await updateRestaurantBookmarkStatus(queryClient, variables.restaurantId);
    },
    onSettled: (_data, _error, variables) => {
      // Add-to-list never refreshed the cached profile; without this it read
      // "not saved" until the detail entry went stale.
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurantId),
        exact: true,
      });
    },
  });
}

/**
 * Hook to add a restaurant to a list by Google Place ID
 */
export function useAddRestaurantWithGoogleId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, googleId }: { urlKey: string; googleId: string }) =>
      apiFetch<void>(`/lists/${urlKey}/add-google-id/`, {
        method: 'PATCH',
        body: { googleId },
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to remove a restaurant from a list with optimistic updates
 */
export function useRemoveRestaurantFromList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, restaurantId }: { urlKey: string; restaurantId: number }) =>
      apiFetch<void>(`/lists/${urlKey}/remove/`, {
        method: 'PATCH',
        body: { restaurantId },
      }),
    onMutate: async ({ urlKey, restaurantId }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: listsKeys.detail(urlKey) });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<RestaurantListDetails>(listsKeys.detail(urlKey));

      // Optimistically update the cache
      if (previousData) {
        const updatedLocations = { ...previousData.locations };

        // Find and remove the restaurant from the appropriate location
        for (const location of Object.keys(updatedLocations)) {
          const restaurants = updatedLocations[location];
          const index = restaurants.findIndex(r => r.id === restaurantId);
          if (index !== -1) {
            updatedLocations[location] = restaurants.filter(r => r.id !== restaurantId);
            // Remove the location key if it's now empty
            if (updatedLocations[location].length === 0) {
              delete updatedLocations[location];
            }
            break;
          }
        }

        // Calculate new restaurant count
        const newRestaurantCount = Object.values(updatedLocations).reduce(
          (sum, restaurants) => sum + restaurants.length,
          0
        );

        queryClient.setQueryData<RestaurantListDetails>(listsKeys.detail(urlKey), {
          ...previousData,
          locations: updatedLocations,
          restaurantCount: newRestaurantCount,
        });
      }

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_, variables, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousData) {
        queryClient.setQueryData(listsKeys.detail(variables.urlKey), context.previousData);
      }
    },
    onSuccess: async (_, variables) => {
      // Track restaurant removed from list
      analytics.capture(AnalyticsEvents.RESTAURANT_REMOVED_FROM_LIST, {
        list_id: variables.urlKey,
        restaurant_id: variables.restaurantId,
      });
      // Clear bookmark in search cache — if this was the last list, icon should go dark.
      // onSettled will invalidate and re-fetch to correct if the restaurant is still in another list.
      await clearRestaurantBookmarkStatus(queryClient, variables.restaurantId);
    },
    onSettled: (_, __, variables) => {
      // Always refetch after error or success to ensure cache is in sync with server
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
      // The profile's isBookmarked may still be true via another list.
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurantId),
        exact: true,
      });
    },
  });
}

/**
 * Hook to share a list (make it public)
 *
 * This is a mutation: it makes the list public, applies the note-sharing choice, and —
 * when note-sharing is turned off — permanently deletes the owner's stale notes-preview
 * feed activities. It is therefore a POST with `shareNotes` in the body (the API client
 * snake_cases it to `share_notes` on the way out). The endpoint rejects GET with a 405 so
 * a replayed or prefetched URL can never mutate list state.
 */
export function useShareList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, shareNotes }: { urlKey: string; shareNotes: boolean }) =>
      apiFetch<ShareListResponse>(`/lists/${urlKey}/share/`, {
        method: 'POST',
        body: { shareNotes } satisfies ShareListRequest,
      }),
    onSuccess: (_, variables) => {
      // Track list shared
      analytics.capture(AnalyticsEvents.LIST_SHARED, {
        list_id: variables.urlKey,
        audience: 'public',
      });

      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to privatize a list
 */
export function usePrivatizeList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<void>(`/lists/${urlKey}/privatize/`, { method: 'PATCH' }),
    onSuccess: (_, urlKey) => {
      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(urlKey) });
    },
  });
}

/**
 * Hook to copy a list
 */
export function useCopyList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, newListName }: { urlKey: string; newListName: string }) =>
      apiFetch<CopyListResponse>(`/lists/${urlKey}/copy/`, {
        method: 'POST',
        body: { newListName },
      }),
    onSuccess: (_, variables) => {
      // Track list copied
      analytics.capture(AnalyticsEvents.LIST_COPIED, {
        source_list_id: variables.urlKey,
      });

      // Invalidate all list queries (lists, search, details, etc.)
      queryClient.invalidateQueries({ queryKey: listsKeys.all });
    },
  });
}

/**
 * Hook to follow a list
 */
export function useFollowList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<void>(`/lists/${urlKey}/follow/`, { method: 'POST' }),
    onSuccess: (_, urlKey) => {
      // Track list followed
      analytics.capture(AnalyticsEvents.LIST_FOLLOWED, {
        list_id: urlKey,
      });

      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(urlKey) });
    },
  });
}

/**
 * Hook to unfollow a list
 */
export function useUnfollowList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<void>(`/lists/${urlKey}/unfollow/`, { method: 'POST' }),
    onSuccess: (_, urlKey) => {
      // Track list unfollowed
      analytics.capture(AnalyticsEvents.LIST_UNFOLLOWED, {
        list_id: urlKey,
      });

      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(urlKey) });
    },
  });
}

/**
 * Hook to reorder locations in a list
 */
export function useReorderLocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & ReorderLocationsRequest) =>
      apiFetch<void>(`/lists/${urlKey}/reorder-locations/`, {
        method: 'PATCH',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to reorder restaurants in a list
 */
export function useReorderRestaurants() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & ReorderRestaurantsRequest) =>
      apiFetch<void>(`/lists/${urlKey}/reorder-restaurants/`, {
        method: 'PATCH',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to update list cover art
 */
export function useUpdateCoverArt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, coverArtUrl }: { urlKey: string; coverArtUrl: string }) =>
      apiFetch<void>(`/lists/${urlKey}/update-cover-art/`, {
        method: 'PATCH',
        body: { coverArtUrl },
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to pin a list to the user's profile
 */
export function usePinList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey }: { urlKey: string }) =>
      apiFetch<void>(`/lists/${urlKey}/pin-to-profile/`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      // Invalidate all list queries so profile pages (byUser) and list pages all refresh
      queryClient.invalidateQueries({ queryKey: listsKeys.all });
    },
  });
}

/**
 * Hook to unpin a list from the user's profile
 */
export function useUnpinList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey }: { urlKey: string }) =>
      apiFetch<void>(`/lists/${urlKey}/unpin-from-profile/`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      // Invalidate all list queries so profile pages (byUser) and list pages all refresh
      queryClient.invalidateQueries({ queryKey: listsKeys.all });
    },
  });
}

/**
 * Hook to add a tag to a list
 */
export function useAddTagToList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, tagId }: { urlKey: string; tagId: number }) =>
      apiFetch<AddTagResponse>(`/lists/${urlKey}/tags/`, {
        method: 'POST',
        body: { tagId },
      }),
    onSuccess: (_, variables) => {
      // Track tag added to list
      analytics.capture(AnalyticsEvents.LIST_TAG_ADDED, {
        list_id: variables.urlKey,
        tag_id: variables.tagId,
      });

      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
      // Also invalidate byTag queries since list membership changed
      queryClient.invalidateQueries({ queryKey: listsKeys.all });
    },
  });
}

/**
 * Hook to remove a tag from a list
 */
export function useRemoveTagFromList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, tagId }: { urlKey: string; tagId: number }) =>
      apiFetch<RemoveTagResponse>(`/lists/${urlKey}/tags/${tagId}/`, {
        method: 'DELETE',
      }),
    onSuccess: (_, variables) => {
      // Track tag removed from list
      analytics.capture(AnalyticsEvents.LIST_TAG_REMOVED, {
        list_id: variables.urlKey,
        tag_id: variables.tagId,
      });

      queryClient.invalidateQueries({ queryKey: listsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: listsKeys.detail(variables.urlKey) });
      // Also invalidate byTag queries since list membership changed
      queryClient.invalidateQueries({ queryKey: listsKeys.all });
    },
  });
}
